import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  listDestinations,
  createDestination,
  updateDestination,
  deleteDestination,
} from '../../../api/schoolConfigApi';
import { ApiError } from '../../../api/http';
import type { components } from '../../../api/types.gen';
import { showSuccess, showError } from '../../../utils/notifications';
import { getContextualError, logError } from '../../../utils/errorHandling';
import CollapsibleSection from '../../common/CollapsibleSection';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import adminStyles from '../AdminPanel.module.css';

/** A destination row as returned by the schools-api destinations endpoints. */
type Destination = components['schemas']['DestinationResponse'];

interface DestinationsPanelProps {
  /**
   * The school whose destinations are managed. Every caller (AdminPanel, the
   * setup wizard, tests) passes it explicitly; the `?? currentUser?.schoolId`
   * fallback below only ever applies when the prop is a nullish runtime value.
   */
  schoolId: number | string;
  /** Render the bare form (wizard step 5) with no CollapsibleSection wrapper. */
  embedded?: boolean;
}

// Ported from AdminPanel.js's "Destination Management" section (JSX lines
// 4460-4656 and legacy handlers addDestination/removeDestination/
// updateDestination, lines 901-1041) as part of the Firebase ->
// hallpass-backend migration.
//
// Scope decision (confirmed): hallpass-backend's Destination model
// (packages/db/prisma/schema.prisma, apps/schools-api/src/schemas/destination.ts)
// only has {id, schoolId, name, maxOccupancy, createdAt, updatedAt,
// deletedAt} — there is no equivalent for the legacy `subcategories`,
// `countsTowardLimit`, or the derived `requiresSubcategory`. Those fields
// (and their add/edit UI) are dropped entirely; `maxOccupancy` (optional,
// nullable) is added in their place.
//
// The Firestore onSnapshot listener on
// `organizationData/{currentOrgId}/destinations` is replaced with an
// explicit fetchDestinations() call on mount and after every successful
// mutation — real-time push is intentionally dropped for this admin config
// data in favor of a plain refetch.

const DestinationsPanel = ({
  schoolId: schoolIdProp,
  embedded = false,
}: DestinationsPanelProps) => {
  const { currentUser } = useAuth();
  const schoolId = schoolIdProp ?? currentUser?.schoolId;

  const [loadingStates, setLoading] = useLoadingStates({
    loadDestinations: false,
    addDestination: false,
    updateDestination: false,
    removeDestination: false,
  });

  const [destinations, setDestinations] = useState<Destination[]>([]);

  // Guards against a stale response clobbering `destinations` for the wrong
  // schoolId: if schoolId changes while a fetch is in flight, the effect
  // below flips `cancelled` on cleanup so an earlier request that resolves
  // after a later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in useAdminSetupChecklist.js's fetchCounts.
  const fetchDestinations = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      if (!schoolId) return;

      if (!isCancelled()) setLoading('loadDestinations', true);
      try {
        const result = await listDestinations(schoolId);
        if (!isCancelled()) {
          setDestinations(
            [...result.data].sort((a, b) => a.name.localeCompare(b.name))
          );
        }
      } catch (error) {
        logError(error, 'loadDestinations');
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              'Failed to load destinations. Please try again.'
            )
          );
          setDestinations([]);
        }
      } finally {
        if (!isCancelled()) setLoading('loadDestinations', false);
      }
    },
    [schoolId, setLoading]
  );

  useEffect(() => {
    let cancelled = false;
    fetchDestinations(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchDestinations]);

  // Add form
  const [newDestinationName, setNewDestinationName] = useState('');
  const [newDestinationMaxOccupancy, setNewDestinationMaxOccupancy] =
    useState('');

  const addDestination = async () => {
    if (!newDestinationName.trim()) {
      showError('Please enter a destination name.');
      return;
    }

    setLoading('addDestination', true);
    try {
      await createDestination(schoolId, {
        name: newDestinationName.trim(),
        maxOccupancy: newDestinationMaxOccupancy
          ? Number(newDestinationMaxOccupancy)
          : null,
      });

      setNewDestinationName('');
      setNewDestinationMaxOccupancy('');
      showSuccess(`Added ${newDestinationName.trim()} to destinations list`);
      await fetchDestinations();
    } catch (error) {
      logError(error, 'addDestination');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to add destination. Please try again.'
        )
      );
    } finally {
      setLoading('addDestination', false);
    }
  };

  // Edit form
  const [editingDestination, setEditingDestination] = useState<number | null>(
    null
  );
  const [editDestinationName, setEditDestinationName] = useState('');
  const [editDestinationMaxOccupancy, setEditDestinationMaxOccupancy] =
    useState('');

  const startEditDestination = (destination: Destination) => {
    setEditingDestination(destination.id);
    setEditDestinationName(destination.name);
    setEditDestinationMaxOccupancy(
      destination.maxOccupancy != null ? String(destination.maxOccupancy) : ''
    );
  };

  const cancelEditDestination = () => {
    setEditingDestination(null);
    setEditDestinationName('');
    setEditDestinationMaxOccupancy('');
  };

  const submitUpdateDestination = async () => {
    // The Update button only renders inside the edit form, where
    // `editingDestination` is the id being edited — never null here. This
    // narrows the type for the updateDestination call and never fires at
    // runtime.
    if (editingDestination == null) return;

    if (!editDestinationName.trim()) {
      showError('Destination name is required.');
      return;
    }

    setLoading('updateDestination', true);
    try {
      await updateDestination(schoolId, editingDestination, {
        name: editDestinationName.trim(),
        maxOccupancy: editDestinationMaxOccupancy
          ? Number(editDestinationMaxOccupancy)
          : null,
      });

      showSuccess('Destination updated.');
      cancelEditDestination();
      await fetchDestinations();
    } catch (error) {
      logError(error, 'updateDestination');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to update destination. Please try again.'
        )
      );
    } finally {
      setLoading('updateDestination', false);
    }
  };

  const removeDestination = async (destination: Destination) => {
    if (!window.confirm(`Remove ${destination.name} from destinations list?`)) {
      return;
    }

    setLoading('removeDestination', true);
    try {
      await deleteDestination(schoolId, destination.id);
      showSuccess(`Removed ${destination.name} from destinations list`);
      await fetchDestinations();
    } catch (error) {
      logError(error, 'removeDestination');
      if (error instanceof ApiError && error.status === 409) {
        showError(
          'This destination has in-flight passes and cannot be removed.'
        );
      } else {
        showError(
          getContextualError(
            error,
            undefined,
            'Failed to remove destination. Please try again.'
          )
        );
      }
    } finally {
      setLoading('removeDestination', false);
    }
  };

  const content = (
    <>
      <div className="destination-management">
        <div className="add-destination-form">
          <h3>Add Destination</h3>
          <div className="form-row">
            <input
              type="text"
              placeholder="Destination name (e.g., Bathroom, Referral)"
              aria-label="Destination name"
              value={newDestinationName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewDestinationName(e.target.value)
              }
              className={adminStyles['teacher-input']}
              disabled={loadingStates.addDestination}
            />
          </div>

          <div className="form-row">
            <input
              type="number"
              min="1"
              placeholder="Max occupancy (optional)"
              aria-label="Max occupancy (optional)"
              value={newDestinationMaxOccupancy}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewDestinationMaxOccupancy(e.target.value)
              }
              className={adminStyles['teacher-input']}
              disabled={loadingStates.addDestination}
            />
          </div>

          <div className={adminStyles['button-group']}>
            <button
              onClick={addDestination}
              disabled={loadingStates.addDestination}
              className={adminStyles['add-btn']}
            >
              {loadingStates.addDestination ? 'Adding...' : 'Add Destination'}
            </button>
          </div>
        </div>
      </div>

      <div className="destinations-list">
        <h3>Current Destinations ({destinations.length})</h3>
        {loadingStates.loadDestinations ? (
          <div className="loading">Loading destinations...</div>
        ) : destinations.length === 0 ? (
          <p>No destinations yet. Add some above.</p>
        ) : (
          <div className={adminStyles['teachers-grid']}>
            {destinations.map((destination) => (
              <div key={destination.id} className={adminStyles['teacher-card']}>
                {editingDestination === destination.id ? (
                  // Edit form
                  <div className="edit-destination-form">
                    <div className="form-group">
                      <label htmlFor={`edit-destination-name-${destination.id}`}>
                        Destination Name:
                      </label>
                      <input
                        id={`edit-destination-name-${destination.id}`}
                        type="text"
                        value={editDestinationName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditDestinationName(e.target.value)
                        }
                        placeholder="Destination name"
                        disabled={loadingStates.updateDestination}
                      />
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor={`edit-destination-max-occupancy-${destination.id}`}
                      >
                        Max Occupancy:
                      </label>
                      <input
                        id={`edit-destination-max-occupancy-${destination.id}`}
                        type="number"
                        min="1"
                        placeholder="Optional"
                        value={editDestinationMaxOccupancy}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditDestinationMaxOccupancy(e.target.value)
                        }
                        disabled={loadingStates.updateDestination}
                      />
                    </div>

                    <div className={adminStyles['button-group']}>
                      <button
                        onClick={submitUpdateDestination}
                        disabled={loadingStates.updateDestination}
                        className={adminStyles['add-btn']}
                      >
                        {loadingStates.updateDestination
                          ? 'Updating...'
                          : 'Update'}
                      </button>
                      <button
                        onClick={cancelEditDestination}
                        disabled={loadingStates.updateDestination}
                        className={adminStyles['remove-btn']}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display card
                  <>
                    <div className={adminStyles['teacher-info']}>
                      <strong>{destination.name}</strong>
                      {destination.maxOccupancy != null && (
                        <div
                          className="text-muted-sm"
                          style={{ marginTop: '4px' }}
                        >
                          Max occupancy: {destination.maxOccupancy}
                        </div>
                      )}
                    </div>
                    <div className={adminStyles['button-group']}>
                      <button
                        onClick={() => startEditDestination(destination)}
                        disabled={loadingStates.removeDestination}
                        className={adminStyles['btn-edit']}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => removeDestination(destination)}
                        disabled={loadingStates.removeDestination}
                        className={adminStyles['remove-btn']}
                      >
                        {loadingStates.removeDestination
                          ? 'Removing...'
                          : 'Remove'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <CollapsibleSection
      title="Destination Management"
      icon="📍"
      id="destination-management"
      defaultExpanded={false}
    >
      {content}
    </CollapsibleSection>
  );
};

export default DestinationsPanel;
